import * as React from 'react';
import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { useAppThemeStore } from '@seaters/data-access';
import classNames from 'classnames';
import type { LexicalEditor as LexicalEditorInstance } from 'lexical';
import { fontSizeTextNodeReplacement, FontSizeTextNode } from './nodes/FontSizeTextNode';
import { ImageNode } from './nodes/ImageNode';
import { VideoNode } from './nodes/VideoNode';
import { EditabilityPlugin } from './plugins/EditabilityPlugin';
import { EmojiPickerPlugin } from './plugins/EmojiPickerPlugin';
import { HtmlSyncPlugin } from './plugins/HtmlSyncPlugin';
import { ImagesPlugin } from './plugins/ImagesPlugin';
import { ListTabIndentationPlugin } from './plugins/ListTabIndentationPlugin';
import { TableActionMenuPlugin } from './plugins/TableActionMenuPlugin';
import { TableCellResizerPlugin } from './plugins/TableCellResizerPlugin';
import { ToolbarPlugin } from './plugins/ToolbarPlugin';
import { VideosPlugin } from './plugins/VideosPlugin';
import { getEffectivePrototypeEnabled } from '../Prototype/prototypeMode';
import { sanitizeEditorHtml } from './sanitizeEditorHtml';
import {
  $setHtmlContent,
  DEFAULT_FONT_FAMILY,
  normalizeEditorHtml,
} from './utils';
import styles from './lexical-editor.module.css';

const editorTheme = {
  heading: {
    h1: styles.heading1,
    h2: styles.heading2,
    h3: styles.heading3,
  },
  image: styles.imageNode,
  link: styles.link,
  list: {
    listitem: styles.listItem,
    nested: {
      list: styles.listNested,
      listitem: styles.listNestedItem,
    },
    ol: styles.listOrdered,
    olDepth: [styles.listOrderedDepth1, styles.listOrderedDepth2],
    ul: styles.listUnordered,
    ulDepth: [styles.listUnorderedDepth1, styles.listUnorderedDepth2],
  },
  paragraph: styles.paragraph,
  quote: styles.quote,
  table: styles.table,
  tableCell: styles.tableCell,
  tableCellHeader: styles.tableCellHeader,
  tableCellSelected: styles.tableCellSelected,
  tableRow: styles.tableRow,
  tableScrollableWrapper: styles.tableScrollableWrapper,
  text: {
    bold: styles.textBold,
    italic: styles.textItalic,
    strikethrough: styles.textStrikethrough,
    underline: styles.textUnderline,
  },
};

export interface LexicalEditorProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
  ariaLabel?: string;
  borderless?: boolean;
  compact?: boolean;
  disabled?: boolean;
  /** Opt in to prototype video: Insert → Video when editing, playable embeds when read-only. Off everywhere else. */
  enableVideo?: boolean;
  height?: number | string;
  initialValue?: string;
  invalid?: boolean;
  /** Read-only only: clamp the content to this many lines with an ellipsis. */
  maxLines?: number;
  onEditorChange?: (value: string, editor: LexicalEditorInstance) => void;
  placeholder?: string;
  toolbar?: boolean;
  /** Trim the toolbar to the essentials and fold the rest into a "More" menu. */
  toolbarCompact?: boolean;
  /**
   * Insertable variables (merge tokens). When provided, the toolbar shows a
   * "Variable" dropdown that inserts the token text at the cursor.
   */
  variables?: { label: string; token: string }[];
  value?: string;
  width?: number | string;
}

export const LexicalEditor = React.forwardRef<
  HTMLDivElement,
  LexicalEditorProps
>(function LexicalEditor(
  {
    ariaLabel = 'Rich text editor',
    borderless = false,
    className,
    compact = false,
    disabled = false,
    enableVideo = false,
    height,
    initialValue,
    invalid = false,
    maxLines,
    onBlur,
    onEditorChange,
    onFocus,
    placeholder = 'Start typing…',
    style,
    toolbar = true,
    toolbarCompact = false,
    variables,
    value,
    width = '100%',
    ...props
  }: LexicalEditorProps,
  ref
) {
  const { mode } = useAppThemeStore();
  const isStaticReadOnly = disabled && toolbar === false;
  const [initialHtml] = React.useState(() =>
    normalizeEditorHtml(value ?? initialValue)
  );
  // LexicalComposer reads initialConfig only on mount, so capture initial editability once.
  const [initialEditable] = React.useState(() => !disabled);
  // Registering VideoNode is what makes an editor able to render a video at all,
  // so the opt-in and the flag have to gate it here too — gating only the toolbar
  // would still let pasted markup render a player on every other editor.
  const [videoEnabled] = React.useState(
    () => enableVideo && getEffectivePrototypeEnabled()
  );
  const editorHeight = height ?? (toolbar ? 250 : undefined);
  const readOnlyHtml = React.useMemo(() => {
    if (!isStaticReadOnly) {
      return '';
    }

    return sanitizeEditorHtml(normalizeEditorHtml(value ?? initialValue), {
      allowVideo: enableVideo,
    });
  }, [enableVideo, initialValue, isStaticReadOnly, value]);

  const initialConfig = React.useMemo(
    () => ({
      editable: initialEditable,
      editorState: (editor: LexicalEditorInstance) => {
        $setHtmlContent(editor, initialHtml);
      },
      namespace: 'SeatersLexicalEditor',
      nodes: [
        FontSizeTextNode,
        fontSizeTextNodeReplacement,
        HeadingNode,
        ImageNode,
        LinkNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        ...(videoEnabled ? [VideoNode] : []),
      ],
      onError: (error: Error) => {
        throw error;
      },
      theme: editorTheme,
    }),
    [initialEditable, initialHtml, videoEnabled]
  );

  return (
    <div
      ref={ref}
      {...props}
      className={classNames(
        styles.root,
        {
          [styles.borderless]: borderless,
          [styles.compact]: compact,
          [styles.invalid]: invalid,
          [styles.previewClickable]:
            disabled && toolbar === false && !!props.onClick,
        },
        className
      )}
      data-theme={mode}
      style={{
        ...style,
        width,
      }}
    >
      {isStaticReadOnly ? (
        <div
          className={classNames(styles.editorShell, {
            [styles.editorShellScrollable]: editorHeight !== undefined,
          })}
          style={
            editorHeight === undefined
              ? undefined
              : {
                  height:
                    typeof editorHeight === 'number'
                      ? `${editorHeight}px`
                      : editorHeight,
                }
          }
        >
          {/* No aria-label: on a plain div it is prohibited ARIA, and the content is read as normal text. */}
          <div
            className={classNames(styles.readOnlyContent, {
              [styles.editorInputDisabled]: disabled,
              [styles.editorInputScrollable]: editorHeight !== undefined,
            })}
            dangerouslySetInnerHTML={{ __html: readOnlyHtml }}
            onBlur={onBlur}
            onFocus={onFocus}
            style={{
              fontFamily: DEFAULT_FONT_FAMILY,
              ...(maxLines && {
                display: '-webkit-box',
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
            }}
          />
        </div>
      ) : (
        <LexicalComposer initialConfig={initialConfig}>
          {toolbar && (
            <ToolbarPlugin
              disabled={disabled}
              compact={toolbarCompact}
              enableVideo={enableVideo}
              variables={variables}
            />
          )}
          <div
            className={classNames(styles.editorShell, {
              [styles.editorShellScrollable]: editorHeight !== undefined,
            })}
            style={
              editorHeight === undefined
                ? undefined
                : {
                    height:
                      typeof editorHeight === 'number'
                        ? `${editorHeight}px`
                        : editorHeight,
                  }
            }
          >
            <RichTextPlugin
              ErrorBoundary={LexicalErrorBoundary}
              contentEditable={
                <ContentEditable
                  aria-label={ariaLabel}
                  className={classNames(styles.editorInput, {
                    [styles.editorInputDisabled]: disabled,
                    [styles.editorInputScrollable]: editorHeight !== undefined,
                  })}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  placeholder={null}
                  style={{
                    fontFamily: DEFAULT_FONT_FAMILY,
                  }}
                />
              }
              placeholder={
                <div className={styles.placeholder}>{placeholder}</div>
              }
            />
          </div>
          <HistoryPlugin />
          <LinkPlugin />
          <ClickableLinkPlugin disabled={!disabled} />
          <ListPlugin />
          <ListTabIndentationPlugin disabled={disabled} />
          <TablePlugin hasHorizontalScroll />
          <TableCellResizerPlugin />
          <TableActionMenuPlugin disabled={disabled} />
          <EmojiPickerPlugin disabled={disabled} />
          <ImagesPlugin />
          {videoEnabled && <VideosPlugin />}
          <EditabilityPlugin disabled={disabled} />
          <HtmlSyncPlugin
            initialHtml={initialHtml}
            onEditorChange={onEditorChange}
            value={value}
          />
        </LexicalComposer>
      )}
    </div>
  );
});

LexicalEditor.displayName = 'LexicalEditor';
